import React, { FC, ReactNode, useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

interface ResizableDocumentViewerProps {
  leftPanel: ReactNode
  rightPanel: ReactNode
}

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.forecast.background1};
  overflow: hidden;
  border-radius: 0.5rem;
`

const Panel = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}%;
  background-color: ${({ theme }) => theme.forecast.background1};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  padding: 1rem;
`

const ResizeHandle = styled.div`
  width: 6px;
  background-color: ${({ theme }) => theme.forecast.border2};
  cursor: col-resize;
  position: relative;
  transition: background-color 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 30px;
    background-color: ${({ theme }) => theme.forecast.secondaryText};
    border-radius: 1px;
    opacity: 0.5;
  }

  &:hover {
    background-color: ${({ theme }) => theme.forecast.primary};

    &::after {
      opacity: 1;
      background-color: ${({ theme }) => theme.forecast.primary};
    }
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.primary};

    &::after {
      opacity: 1;
      background-color: ${({ theme }) => theme.forecast.primary};
    }
  }
`

const MIN_PANEL_WIDTH_PERCENT = 30
const DEFAULT_LEFT_WIDTH_PERCENT = 40

export const ResizableDocumentViewer: FC<ResizableDocumentViewerProps> = ({
  leftPanel,
  rightPanel,
}) => {
  const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT_WIDTH_PERCENT)
  const containerRef = useRef<HTMLDivElement>(null)
  const isResizingRef = useRef(false)

  const handleMouseDown = useCallback(() => {
    isResizingRef.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current || !containerRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const containerWidth = containerRect.width
    const mouseX = e.clientX - containerRect.left

    // Calculate new left width as percentage
    let newLeftWidthPercent = (mouseX / containerWidth) * 100

    // Enforce minimum widths
    newLeftWidthPercent = Math.max(
      MIN_PANEL_WIDTH_PERCENT,
      Math.min(100 - MIN_PANEL_WIDTH_PERCENT, newLeftWidthPercent)
    )

    setLeftWidth(newLeftWidthPercent)
  }, [])

  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [])

  React.useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])

  const rightWidth = 100 - leftWidth

  return (
    <Container ref={containerRef}>
      <Panel $width={leftWidth}>{leftPanel}</Panel>
      <ResizeHandle onMouseDown={handleMouseDown} />
      <Panel $width={rightWidth}>{rightPanel}</Panel>
    </Container>
  )
}
